"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackInteraction } from "@/utils/trackInteraction";
import { getCookieValue } from "@/utils/getCookieValue";
import { getMetaData } from "@/utils/getMetaData";

interface PageViewTrackerProps {
  requestPerms?: boolean;
}

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return "mobile";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  return "desktop";
};

const getGeolocation = (): Promise<Partial<GeolocationCoordinates>> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => resolve({}),
      { timeout: 1000 },
    );
  });
};

const getPermissions = async (): Promise<Record<string, string>> => {
  const permissions: Record<string, string> = {};
  const types = [
    "geolocation",
    "notifications",
    "camera",
    "microphone",
    "clipboard-read",
  ];
  for (const name of types) {
    try {
      const result = await navigator.permissions.query({
        name: name as PermissionName,
      });
      permissions[name] = result.state;
    } catch {
      permissions[name] = "unsupported";
    }
  }
  return permissions;
};

const getUrlParams = (): Record<string, string> => {
  const params: Record<string, string> = {};
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  urlParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

const SESSION_REFERRER_KEY = "isov_referrer";

export default function usePageViewTracker({
  requestPerms,
}: PageViewTrackerProps) {
  const pathname = usePathname();

  useEffect(() => {
    const trackPageView = async () => {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );

      const currentUrl = window.location.href;
      const urlParams = getUrlParams();
      const utmReferrer =
        urlParams.utm_source && urlParams.utm_medium
          ? `utm:${urlParams.utm_source}/${urlParams.utm_medium}`
          : urlParams.utm_source
            ? `utm:${urlParams.utm_source}`
            : null;

      const storedReferrer = sessionStorage.getItem(SESSION_REFERRER_KEY);
      const pageReferrer =
        storedReferrer || document.referrer || utmReferrer || "direct";

      const data: Record<string, any> = {
        event_label: "page_view",
        page_path: pathname,
        page_url: currentUrl,
        page_title: document.title,
        referrer: pageReferrer,
        url_params: urlParams,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        language: navigator.language,
        user_agent: navigator.userAgent,
        platform: navigator.platform || "unknown",
        device_type: getDeviceType(),
        device_memory: (navigator as any).deviceMemory ?? "unknown",
        hardware_concurrency: navigator.hardwareConcurrency || "unknown",
        cookies_enabled: navigator.cookieEnabled,
        js_enabled: true,
        do_not_track: navigator.doNotTrack === "1",
        pixel_ratio: window.devicePixelRatio,
        color_depth: window.screen.colorDepth,
        timestamp: new Date().toISOString(),
        identity: getCookieValue("hubspotutk"),
        content_tags: getMetaData("content-tag"),
      };

      const connection = (navigator as any).connection || {};
      data.connection_type = connection.effectiveType || "unknown";
      data.downlink = connection.downlink ?? "unknown";
      data.rtt = connection.rtt ?? "unknown";
      data.save_data_mode = connection.saveData ?? false;

      const [geo, perms] = requestPerms
        ? await Promise.all([getGeolocation(), getPermissions()])
        : [];

      trackInteraction(
        {
          ...data,
          ...geo,
          permissions: perms,
        },
        "page_view",
      );

      sessionStorage.setItem(SESSION_REFERRER_KEY, currentUrl);
    };

    trackPageView().catch(() => {});
  }, [pathname, requestPerms]);
}
